/**
 * `useAgentCardProfileApply` — dashboard hook for the merchant-
 * authoritative apply path (ADR-0114 Slice B3, see ADR-0114 §5.1 and
 * §7).
 *
 * Wraps {@link AgentCardProfileAPI.apply} with the suggestions-modal
 * lifecycle:
 *
 *   - `apply({ site_id, etag, fields, source_hint? })` POSTs to
 *     `/v1/sites/:site_id/agent-card-profile`. On 200 the hook
 *     returns success + the new visibility report + the new eTag the
 *     dashboard threads into the next If-Match.
 *   - On 412 (`concurrency_mismatch`) the hook surfaces the
 *     `stale_etag` state so the modal can refresh suggestions and
 *     re-prompt without closing.
 *   - On 403 / 400 / 404 the hook returns the failure verbatim; the
 *     modal renders the right inline banner.
 *
 * Untrusted-rendering posture (B3 review surface): the hook handles
 * the wire only. The suggested `value` / `rationale` strings are
 * never passed to `dangerouslySetInnerHTML`; they pass through the
 * apply body unchanged (the server re-validates + sanitizes). The
 * modal renders them as JSX text under a "Suggested by Sill's site
 * analyzer" badge.
 *
 * Multi-language posture (ADR-0114 §1): the hook is language-agnostic
 * — values + rationales are byte strings, the wire treats them
 * uniformly, and the server's sanitizer strips BiDi-override hazards
 * while preserving the source language's natural BiDi marks. The
 * modal is responsible for `dir="auto"` rendering of CJK / RTL.
 *
 * No polling, no retry. The hook is one-shot per Apply click;
 * Re-clicks fire a fresh POST. The modal owns the busy state through
 * the returned `pending` flag.
 */

import { useCallback, useRef, useState } from 'react';

import type { SiteId } from '@sill/types';

import {
  type AgentCardProfileAPI,
  type ApplyAgentCardProfileFailureKind,
  type ApplyAgentCardProfileResult,
  type ApplyFieldName,
  type ApplySourceHint,
} from '../api/agent-card-profile.js';

export interface UseAgentCardProfileApplyDeps {
  /** The bag instance. In production this is wired by the provider;
   *  in tests the consumer passes a stub. */
  readonly api: AgentCardProfileAPI;
}

export interface ApplyArgs {
  readonly site_id: SiteId;
  readonly etag: string;
  readonly fields: Partial<Record<ApplyFieldName, string>>;
  readonly source_hint?: ApplySourceHint;
}

export interface UseAgentCardProfileApplyState {
  /** True while a POST is in flight. The modal disables the apply
   *  button + checkboxes while pending. */
  readonly pending: boolean;
  /** The failure kind from the last apply, if it failed. Cleared on
   *  the next apply() call OR on an explicit {@link clearFailure}. */
  readonly lastFailure:
    | {
        kind: ApplyAgentCardProfileFailureKind;
        server_reason?: string;
        field?: string;
      }
    | null;
  apply(args: ApplyArgs): Promise<ApplyAgentCardProfileResult>;
  /** Reset the stored `lastFailure` to null. Used by the 412 recovery
   *  path (founder feedback #6, 2026-06-18): clicking "Refresh
   *  suggestions" must clear the stale-eTag banner immediately so the
   *  merchant sees the refresh took effect, instead of waiting for
   *  the next apply call (which is when the hook's own reset would
   *  fire). Without this, the banner persisted after a successful
   *  refresh and the merchant read it as "the button did nothing." */
  clearFailure(): void;
}

export function useAgentCardProfileApply(
  deps: UseAgentCardProfileApplyDeps,
): UseAgentCardProfileApplyState {
  const [pending, setPending] = useState<boolean>(false);
  const [lastFailure, setLastFailure] = useState<
    UseAgentCardProfileApplyState['lastFailure']
  >(null);
  /** Mounted flag — protect state updates after unmount. */
  const mounted = useRef<boolean>(true);

  const apply = useCallback(
    async (args: ApplyArgs): Promise<ApplyAgentCardProfileResult> => {
      setPending(true);
      setLastFailure(null);
      const result = await deps.api.apply(args);
      if (mounted.current) {
        setPending(false);
        if (result.kind !== 'ok') {
          setLastFailure({
            kind: result.kind,
            ...(result.server_reason !== undefined
              ? { server_reason: result.server_reason }
              : {}),
            ...(result.field !== undefined ? { field: result.field } : {}),
          });
        }
      }
      return result;
    },
    [deps.api],
  );

  const clearFailure = useCallback((): void => {
    if (mounted.current) setLastFailure(null);
  }, []);

  // We never set `mounted` to false in a returned cleanup intentionally
  // — the hook is render-then-call so the unmount path is the consumer's
  // responsibility. Tests can directly assert against the returned
  // promise.

  return { pending, lastFailure, apply, clearFailure };
}
